import { useState } from 'react';
import { Modal } from 'antd';
import { donemAraligiMetni, type Donem } from '../donem';
import { tarihEtiketi } from '../ZamanBlok';
import type { DetayGovdeProps, SaDetay } from './detayTipleri';
import { KategoriDetay } from './KategoriDetay';
import { DigerKategorilerDetay } from './DigerKategorilerDetay';
import { KanalDetay } from './KanalDetay';
import { SubeDetay } from './SubeDetay';
import { GunDetay } from './GunDetay';
import { ZararDetay } from './ZararDetay';
import { IadeDetay } from './IadeDetay';
import { UrunDetay } from './UrunDetay';
import { TumUrunlerDetay } from './TumUrunlerDetay';

export function detayNesnesi(d: SaDetay): string {
  switch (d.tur) {
    case 'kategori':
      return d.kategori;
    case 'digerKategoriler':
      return 'Diğer kategoriler';
    case 'kanal':
      return d.kanal;
    case 'sube':
      return d.sube;
    case 'gun':
      return tarihEtiketi(d.gun);
    case 'zarar':
      return 'Zararına satış';
    case 'iade':
      return 'İade oranı';
    case 'tumUrunler':
      return 'Tüm ürünler';
    case 'urun':
      return d.urunAdi;
  }
}

/**
 * "<nesne> · <donem araligi> · <sube> · <kanal>" (spec §7.5). Gun detayinda pencere o gundur -> aralik yazilmaz;
 * kanal/sube detayinda nesnenin kendisi olan suzgec tekrar yazilmaz; bos suzgec yazilmaz.
 */
export function detayBasligi(d: SaDetay, donem: Donem, sube: string | null, kanal: string | null): string {
  const parcalar = [detayNesnesi(d)];
  if (d.tur !== 'gun') parcalar.push(donemAraligiMetni(donem));
  if (sube !== null && d.tur !== 'sube') parcalar.push(sube);
  if (kanal !== null && d.tur !== 'kanal') parcalar.push(kanal);
  return parcalar.join(' · ');
}

function DetayGovde({ d, ...p }: { d: SaDetay } & DetayGovdeProps) {
  switch (d.tur) {
    case 'kategori':
      return <KategoriDetay {...p} kategori={d.kategori} />;
    case 'digerKategoriler':
      return <DigerKategorilerDetay {...p} />;
    case 'kanal':
      return <KanalDetay {...p} kanalAdi={d.kanal} />;
    case 'sube':
      return <SubeDetay {...p} subeAdi={d.sube} />;
    case 'gun':
      return <GunDetay {...p} gun={d.gun} />;
    case 'zarar':
      return <ZararDetay {...p} />;
    case 'iade':
      return <IadeDetay {...p} />;
    case 'tumUrunler':
      return <TumUrunlerDetay {...p} />;
    case 'urun':
      return <UrunDetay {...p} stokKodu={d.stokKodu} urunAdi={d.urunAdi} />;
  }
}

type Props = { detay: SaDetay | null; onKapat: () => void } & Omit<DetayGovdeProps, 'ac'>;

/**
 * G2b/K7 — Satis Analiz detay modali (Yerlice Fatura Listesi deseni: ortada modal). `detay` SAYFANIN state'idir;
 * modal ici gecisler (kategori -> urun) yalniz burada, geri yigininda tutulur. Sayfa yeni bir detay verince
 * (ya da kapatip acinca) yigin sifirlanir.
 *
 * 🔴 Govde YALNIZ etkin detay varken mount olur (`aktif &&`): kapali modal hicbir sorgu acmaz ve kapanis
 * animasyonu surerken govde sorgu tutmaz. `destroyOnHidden` antd kabugunu da kapaninca atar.
 *
 * CONTROLLER RULING S4 (spec §7.5 "telefonda tam genislik"): antd 5.29 `Modal.width` Breakpoint nesnesi kabul
 * eder (`Partial<Record<Breakpoint, string|number>>`, antd/es/modal/interface.d.ts) — `xs`/`sm` CSS
 * degiskenlerine (`--ant-modal-xs-width`/`--ant-modal-sm-width`) yazilir, gercek genislik SAF CSS media
 * query ile secilir (antd/es/modal/style/index.js). jsdom media query calistirmaz; test yalniz degiskenleri
 * dogrular, GERCEK genislik tarayicida (Bolum D) olculur.
 */
export function DetayModal({ detay, onKapat, ...govde }: Props) {
  const [kok, setKok] = useState<SaDetay | null>(detay);
  const [yigin, setYigin] = useState<SaDetay[]>([]);
  if (detay !== kok) {
    // React'in "onceki prop'u state'te tut" kalibi: render sirasinda ayni bilesenin state'i guncellenir.
    setKok(detay);
    setYigin([]);
  }
  const aktif = yigin.length > 0 ? yigin[yigin.length - 1] : detay;
  const ac = (d: SaDetay) => setYigin((y) => [...y, d]);
  const geri = () => setYigin((y) => y.slice(0, -1));

  return (
    <Modal
      open={detay !== null}
      onCancel={onKapat}
      footer={null}
      centered
      width={{ xs: '100%', sm: 'min(960px, 94vw)' }}
      destroyOnHidden
      styles={{ body: { maxHeight: '75vh', overflowY: 'auto' } }}
      title={
        aktif && (
          <div className="flex items-center gap-2 min-w-0 pr-6">
            {yigin.length > 0 && (
              <button
                type="button"
                data-testid="detay-geri"
                onClick={geri}
                className="focus-ring shrink-0 text-[12px] font-normal text-ink-500 hover:text-ink-900"
              >
                ← Geri
              </button>
            )}
            <span data-testid="detay-baslik" className="truncate">
              {detayBasligi(aktif, govde.donem, govde.sube, govde.kanal)}
            </span>
          </div>
        )
      }
    >
      {aktif && (
        <div data-testid="detay-modal" data-tur={aktif.tur}>
          <DetayGovde key={`${yigin.length}-${aktif.tur}`} d={aktif} {...govde} ac={ac} />
        </div>
      )}
    </Modal>
  );
}
